import { AxiosError, AxiosResponse } from "axios";
import { IResponse, IServerError } from "@websential/cosmic-v20";

import { IDropDownListResponse } from "../models/common/IDropDownListResponse";
import { IGetListRequest } from "../models";
import { IROEListModel } from "../models/roe/IROEListModel";
import { IROERequestPayload } from "../models/roe/IROERequestPayload";
import http from "../common/http-commons";
import { IROEWeb } from "../models/ROEWeb";

export const ROEService = {
  getROEById: async (
    id: string | undefined,
    clientId: string,
    taxYear: number,
  ): Promise<any | IServerError | IResponse | null> => {
    if (id) {
      try {
        const response: AxiosResponse<any> = await http.get<any>(
          `/v1/roe/${id}/`,
        );

        if (response.status === 200) {
          return response.data;
        } else if (response.status === 400) {
          return response.data;
        } else if (response.status === 500) {
          return {
            status: false,
            message: "Something went wrong.",
            error: true,
          };
        }

        return {
          status: false,
          message: "Something went wrong please try again later.",
        };
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("An error occurred:", axiosError);
        const errorMessage =
          axiosError.response && typeof axiosError.response.data === "string"
            ? axiosError.response.data
            : "Unknown error";
        return {
          status: false,
          message: errorMessage,
        };
      }
    }

    return null;
  },
  getResourceRoeDropDownList: async (
    resourceId: string,
    clientId: string,
    taxYear: number,
  ): Promise<IDropDownListResponse[] | IServerError | IResponse> => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >(
      `/v1/roe/ddl-roe/${resourceId}/?client_id=${clientId}&tax_year=${taxYear}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  delROE: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    const response: AxiosResponse<string> = await http.delete<string>(
      `/v1/t4-slips/${requestPayload.roeId}/?client_id=${requestPayload.clientId}&tax_year=${requestPayload.taxYear}`,
    );

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    } else if (response.status === 400) {
      return response.data;
    }
    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  delBulkSlips: async (
    requestPayload: any,
  ): Promise<string | IServerError | IResponse> => {
    const response = await http.delete<string>(
      `/v1/t4-slips/bulk-delete/${requestPayload.clientId}/?tax_year=${requestPayload.taxYear}`,
      { data: requestPayload.ids },
    );
    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getList: async (
    clientId: string,
    taxYear: number,
    params: IGetListRequest,
  ): Promise<IROEListModel[] | IServerError | IResponse> => {
    const response: AxiosResponse<IROEListModel[]> = await http.get<
      IROEListModel[]
    >(
      `/v1/t4-slips/?client_id=${clientId}&tax_year=${taxYear}&order_by=${params.order_by}&order_direction=${params.order_direction}&page=${params.page}&size=${params.size}&search_query=${params.search_query}`,
    );

    if (response.status === 200) {
      return response.data;
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  create: async (
    requestPayload: IROERequestPayload,
  ): Promise<IROERequestPayload | IServerError | IResponse | any> => {
    if (requestPayload.id) {
      const response: AxiosResponse<IROERequestPayload> =
        await http.put<IROERequestPayload>(
          // `/v1/t4-slips/${requestPayload.id}/?client_id=${requestPayload.clientId}&tax_year=${requestPayload.taxYear}`,
          // requestPayload
          `/v1/roe/${requestPayload.id}/?start_date=${requestPayload.startDate}&end_date=${requestPayload.endDate}`,
          requestPayload,
        );

      if (response.status === 200) {
        return { status: response.status, data: response.data };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
    }

    const response: AxiosResponse<IROERequestPayload> =
      await http.post<IROERequestPayload>(
        `/v1/roe/roe/?client_id=${requestPayload.clientId}&employee_id=${requestPayload.employeeId}&start_date=${requestPayload.startDate}&end_date=${requestPayload.endDate}`,
        requestPayload,
      );

    if (response.status === 201) {
      return { status: response.status, data: response.data };
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    } else if (response.status === 400) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  getEmployeeDDL: async (
    clientId: string,
    taxYear: number,
  ): Promise<any | IServerError | IResponse | null> => {
    const response: AxiosResponse<IDropDownListResponse[]> = await http.get<
      IDropDownListResponse[]
    >(
      // `/v1/resources/ddl-list/${dataRole}/by-year/?client_id=${clientId}&tax_year=${taxYear}`
      `/v1/employees/ddl?client_id=${clientId}`,
    );

    if (response.status === 200) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },
  getResourceInformationById: async (
    resourceId: string | undefined,
    clientId: string,
    roleName: string,
  ): Promise<any | IServerError | IResponse | null> => {
    if (resourceId) {
      const response: AxiosResponse<any> = await http.get<any>(
        // `/v1/resources/${roleName}/${resourceId}/?client_id=${clientId}`
        `/v1/employees/${resourceId}/`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    }

    return null;
  },

  getROELoadData: async (
    clientId: string,
    employeeId: string,
    startDate: string | null,
    endDate: string | null,
  ): Promise<any | IServerError | IResponse | null> => {
    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/roe/load-data/?" +
          "&client_id=" +
          clientId +
          "&employee_id=" +
          employeeId +
          "&start_date=" +
          startDate +
          "&end_date=" +
          endDate,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }

    return null;
  },

  // ROE List Services //
  getROEListData: async (
    runGetListAPICall: boolean,
    clientId: string,
    employeeId: string,
    roeStatus: string,
    data: IGetListRequest,
  ): Promise<any | IServerError | IResponse | null> => {
    if (!runGetListAPICall || !employeeId) return null;
    // const fixedData: any = {
    // 	page: 1,
    // 	size: 50,
    // 	total: 2,
    // 	items: [
    // 		{
    // 			id: 1,
    // 			name: "one",
    // 			clientId: "69304714892fdf04c186d228",
    // 			taxYear: 2025,
    // 			employeeId: "693049ef892fdf04c186d22e",
    // 			Doh: "Nov 01 2022",
    // 			firstDay: "Jan 01 2025",
    // 			DoT: "N/A",
    // 			lastDay: "Feb 02 2025",
    // 			lastPeriod: "Jan 05 2025",
    // 			dueDate: "Jan 10 2025",
    // 			status: "Ready",
    // 		},
    // 		{
    // 			id: 2,
    // 			name: "two",
    // 			clientId: "69304714892fdf04c186d228",
    // 			taxYear: 2025,
    // 			employeeId: "693049ef892fdf04c186d22d",
    // 			Doh: "Nov 02 2023",
    // 			firstDay: "Jan 02 2025",
    // 			DoT: "N/A",
    // 			lastDay: "Feb 02 2025",
    // 			lastPeriod: "Jan 05 2025",
    // 			dueDate: "Jan 10 2025",
    // 			status: "Ready",
    // 		},
    // 	],
    // 	keyField: {
    // 		field: "id",
    // 		templateUrl: "/t4-reports/{id}",
    // 	},
    // 	headerColumns: [
    // 		{
    // 			field: "name",
    // 			headerName: "Name",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 0,
    // 			frozenColumn: true,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "Doh",
    // 			headerName: "Date of Hire",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 1,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "firstDay",
    // 			headerName: "1st Day",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 1,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "DoT",
    // 			headerName: "DoT",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 0,
    // 			frozenColumn: true,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "lastDay",
    // 			headerName: " Last Day",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 1,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "lastPeriod",
    // 			headerName: "Last Period",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 1,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "dueDate",
    // 			headerName: "Due Date",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 1,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 		{
    // 			field: "status",
    // 			headerName: " Status",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 1,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: 150,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 	],
    // };

    // return new Promise((resolve) => setTimeout(() => resolve(fixedData), 1000));

    try {
      const response: AxiosResponse<any> = await http.get<any>(
        "/v1/roe/?client_id=" +
          clientId +
          "&employee_id=" +
          employeeId +
          "&roe_status=" +
          roeStatus +
          "&search_query=" +
          data.search_query +
          "&order_by=" +
          data.order_by +
          "&order_direction=" +
          data.order_direction +
          "&page=" +
          data.page +
          "&size=" +
          data.size,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
    return null;
  },

  // ROE Web Services //
  createROEWebXml: async (
    payload: any,
  ): Promise<IServerError | IResponse | any> => {
    try {
      const response: AxiosResponse | any = await http.post(
        `/v1/roe-xml/roe/${payload.clientId}/?file_name=${payload.fileName}&web_processing_method=${payload.webProcessingMethod}`,
        {
          roe_ids: payload.roeIds,
        },
      );

      if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        return response;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      return {
        status: false,
        message: errorMessage,
      };
    }
  },

  updatedROEWebMarkAsSubmitted: async (
    roeIds: string[],
  ): Promise<IServerError | IResponse | null> => {
    {
      const response: AxiosResponse | any = await http.put(
        `/v1/roe/update-roe/by-web/`,
        { roeIds },
      );

      if (response.status === 201) {
        return response.data;
      } else if (response.status === 400) {
        return response;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
    }
    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  createROEWebFile: async (
    requestPayload: any,
  ): Promise<any | IServerError | IResponse | any> => {
    // if (requestPayload.id) {
    // 	const response: AxiosResponse<any> = await http.put<any>(
    // 		`/v1/roe-xml/roe/${requestPayload.clientId}?employee_id=${requestPayload.employeeId}&file_name=${requestPayload.fileName}&type=${requestPayload.type}`,
    // 		requestPayload,
    // 	);

    // 	if (response.status === 200) {
    // 		return { status: response.status, data: response.data };
    // 	} else if (response.status === 500) {
    // 		return {
    // 			status: false,
    // 			message: "Something went wrong.",
    // 			error: true,
    // 		};
    // 	}
    // }
    const response: AxiosResponse<any> = await http.post<any>(
      `/v1/roe-xml/roe/${requestPayload.clientId}?employee_id=${requestPayload.employeeId}&file_name=${requestPayload.fileName}&type=${requestPayload.type}`,
      requestPayload,
    );

    if (response.status === 201) {
      return { status: response.status, data: response.data };
    } else if (response.status === 500) {
      return {
        status: false,
        message: "Something went wrong.",
        error: true,
      };
    } else if (response.status === 400) {
      return response.data;
    }

    return {
      status: false,
      message: "Something went wrong please try again later.",
    };
  },

  getROEWebDDLData: async (
    clientId: string,
    // data: IGetListRequest,
    navigateToLogout: (path: string) => void,
  ): Promise<
    IROERequestPayload | IServerError | IResponse | IROERequestPayload[]
  > => {
    // const fixedData: any = {
    // 	page: 1,
    // 	size: 25,
    // 	total: 1,
    // 	items: [
    // 		{
    // 			ids: "1",
    // 			fileName: "Dev",
    // 			id: "694a69f635dcc81bf8446d74",
    // 			status:300
    // 		},
    // 		{
    // 			ids: "2",
    // 			fileName: "Dehv",
    // 			id: "694a69f635dcc81bd8446d74",
    // 			status:100
    // 		},
    // 		{
    // 			ids: "3",
    // 			fileName: "hDev",
    // 			id: "694a69f635dcc8tbc8446d74",
    // 			status:200
    // 		},
    // 	],
    // 	keyField: {
    // 		field: "id",
    // 		templateUrl: "/department/setup/id",
    // 	},
    // 	headerColumns: [
    // 		{
    // 			field: "fileName",
    // 			headerName: "File Name",
    // 			description: "",
    // 			flex: 1,
    // 			hide: false,
    // 			hideable: false,
    // 			sortable: true,
    // 			sortingOrder: "asc",
    // 			resizable: false,
    // 			type: "string",
    // 			align: "left",
    // 			headerAlign: "left",
    // 			hideSortIcons: false,
    // 			disableColumnMenu: true,
    // 			disableReorder: true,
    // 			disableExport: true,
    // 			maskable: false,
    // 			isFixed: true,
    // 			isEditable: true,
    // 			sequence: 0,
    // 			frozenColumn: false,
    // 			footer: null,
    // 			minWidth: null,
    // 			maxWidth: null,
    // 			fieldName: null,
    // 			edr: "edr",
    // 		},
    // 	],
    // };
    // return new Promise((resolve) => setTimeout(() => resolve(fixedData), 1000));
    try {
      const response: AxiosResponse<IROERequestPayload> =
        await http.get<IROERequestPayload>(
          "/v1/roe/roe-ddl?client_id=" + clientId,
        );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 400) {
        return {
          status: false,
          message: "Bad Request.",
          error: true,
        };
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }
      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  getById: async (
    // change api Url later
    clientId: string,
    id: string | undefined,
    navigateToLogout: (path: string) => void,
  ): Promise<IROEWeb | IServerError | IResponse | null | any> => {
    if (!id) return null;
    try {
      const response: AxiosResponse<IROEWeb> = await http.get<IROEWeb>(
        `/v1/roe/${id}/?client_id=${clientId}`,
      );

      if (response.status === 200) {
        return response.data;
      } else if (response.status === 500) {
        return {
          status: false,
          message: "Something went wrong.",
          error: true,
        };
      }

      return {
        status: false,
        message: "Something went wrong please try again later.",
      };
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("An error occurred:", axiosError);
      const errorMessage =
        axiosError.response && typeof axiosError.response.data === "string"
          ? axiosError.response.data
          : "Unknown error";
      if (axiosError && axiosError.status === 401) {
        navigateToLogout("/logout");
        return {
          status: false,
          message: "UnAuthrized",
        };
      } else {
        return {
          status: false,
          message: errorMessage,
        };
      }
    }
  },

  // Web ROE Submission //

  getWebROESubmissionData: async (
    runGetListAPICall: boolean,
    accountingProfessional: string,
    showEmployees: string,
    company: string,
    accountType: string,
    showArchive: boolean,
    data: IGetListRequest,
  ): Promise<any | IServerError | IResponse | null> => {
    if (!runGetListAPICall) return null;
    const fixedData: any = {
      page: 1,
      size: 50,
      total: 3,
      items: [
        {
          id: 1,
          businessNo: "1235434",
          legalName: "WEB ROE 2020",
          operatingName: null,
          postAndZip: "a9a9a9",
          employee: "Alimony Salary",
          payPeriod: "Monthly",
          DOH: "Jan 01 2019",
          DOT: "Sep 30 2025",
          roeStartDate: "Jan  01 2019",
          roeEndDate: "Sep 29 2019",
          dueDate: "Oct 04 2019",
          submissionDate: "Apr 29 2021",
          pdfAtachment: null,
          rejectReason: null,
        },
        {
          id: 2,
          businessNo: "1255434",
          legalName: "WEB ROE 2020",
          operatingName: null,
          postAndZip: "a9a9a9",
          employee: "Alimony Salary",
          payPeriod: "Monthly",
          DOH: "Jan 01 2019",
          DOT: "Sep 30 2025",
          roeStartDate: "Jan  01 2019",
          roeEndDate: "Sep 29 2019",
          dueDate: "Oct 04 2019",
          submissionDate: "Apr 29 2021",
          pdfAtachment: null,
          rejectReason: null,
        },
        {
          id: 3,
          businessNo: "1275434",
          legalName: "WEB ROE 2020",
          operatingName: null,
          postAndZip: "a9a9a9",
          employee: "Alimony Salary",
          payPeriod: "Monthly",
          DOH: "Jan 01 2019",
          DOT: "Sep 30 2025",
          roeStartDate: "Jan  01 2019",
          roeEndDate: "Sep 29 2019",
          dueDate: "Oct 04 2019",
          submissionDate: "Apr 29 2021",
          pdfAtachment: null,
          rejectReason: null,
        },
      ],
      keyField: {
        field: "id",
        templateUrl: "/web-roe/{id}",
      },
      headerColumns: [
        {
          field: "businessNo",
          headerName: "Business No",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 0,
          frozenColumn: true,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "legalName",
          headerName: "Legal Name",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "operatingName",
          headerName: "Operating Name",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "postAndZip",
          headerName: "Post/Zip",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "employee",
          headerName: "Employee",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "payPeriod",
          headerName: "Pay Period",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "DOH",
          headerName: "DOH",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: null,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "DOT",
          headerName: " DOT",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "roeStartDate",
          headerName: "ROE Start Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "roeEndDate",
          headerName: "ROE End Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "dueDate",
          headerName: "Due Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "submissionDate",
          headerName: "Submission Date",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "pdfAttachment",
          headerName: "PDF Attachment",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: 250,
          maxWidth: 250,
          fieldName: null,
          edr: "edr",
        },
        {
          field: "rejectReason",
          headerName: "Reject Reason",
          description: "",
          flex: 1,
          hide: false,
          hideable: false,
          sortable: true,
          sortingOrder: "asc",
          resizable: false,
          type: "string",
          align: "left",
          headerAlign: "left",
          hideSortIcons: false,
          disableColumnMenu: true,
          disableReorder: true,
          disableExport: true,
          maskable: false,
          isFixed: true,
          isEditable: true,
          sequence: 1,
          frozenColumn: false,
          footer: null,
          minWidth: null,
          maxWidth: 150,
          fieldName: null,
          edr: "edr",
        },
      ],
    };

    return new Promise((resolve) => setTimeout(() => resolve(fixedData), 1000));

    // 	try {
    // 		const response: AxiosResponse<any> = await http.get<any>(
    // 			"/v1/roe/?client_id=" +
    // 				clientId +
    // 				"&employee_id=" +
    // 				employeeId +
    // 				"&roe_status=" +
    // 				roeStatus +
    // 				"&search_query=" +
    // 				data.search_query +
    // 				"&order_by=" +
    // 				data.order_by +
    // 				"&order_direction=" +
    // 				data.order_direction +
    // 				"&page=" +
    // 				data.page +
    // 				"&size=" +
    // 				data.size,
    // 		);

    // 		if (response.status === 200) {
    // 			return response.data;
    // 		} else if (response.status === 400) {
    // 			return response.data;
    // 		} else if (response.status === 500) {
    // 			return {
    // 				status: false,
    // 				message: "Something went wrong.",
    // 				error: true,
    // 			};
    // 		}

    // 		return {
    // 			status: false,
    // 			message: "Something went wrong please try again later.",
    // 		};
    // 	} catch (error) {
    // 		const axiosError = error as AxiosError;
    // 		console.error("An error occurred:", axiosError);
    // 		const errorMessage =
    // 			axiosError.response && typeof axiosError.response.data === "string"
    // 				? axiosError.response.data
    // 				: "Unknown error";
    // 		return {
    // 			status: false,
    // 			message: errorMessage,
    // 		};
    // 	}
    // 	return null;
    // },
  },
};
